import { type InputHTMLAttributes, forwardRef } from 'react';
import { FormField, type FormFieldControlProps } from './FormField';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  /**
   * Optional help text rendered below the control (additive — defaults off;
   * see FormField). Not previously supported by Input; safe to add because
   * there is no native `help` HTML attribute to collide with.
   */
  help?: string;
}

// #14006 — Input refactored to compose FormField internally, so all form
// controls share one field-chrome implementation (label / required / help /
// error / aria wiring). Behaviour-preserving: same public props, same
// rendered DOM + aria for every existing call site (see below for the one
// dead-code edge case and why `required?` was deliberately NOT added).
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, help, id, className = '', ...props }, ref) => {
    return (
      <FormField label={label} htmlFor={id} help={help} error={error}>
        {(controlProps: FormFieldControlProps) => {
          // Preserve the original id semantics exactly: when neither `id`
          // nor `label` is supplied, the legacy Input rendered no `id`
          // attribute at all (inputId was `undefined`). FormField always
          // resolves a control id (falling back to `useId()`), so we only
          // adopt FormField's id when the original formula would have
          // produced one (id given, or label given) — otherwise we force
          // `undefined`, matching today's DOM exactly.
          const inputId = id || label ? controlProps.id : undefined;
          return (
            <input
              ref={ref}
              id={inputId}
              className={`h-9 w-full rounded-md border px-3 text-sm transition-colors
                ${error ? 'border-red-500 focus:ring-red-500/50' : 'border-gray-300 focus:ring-indigo-500/50 focus:border-indigo-500'}
                focus:outline-none focus:ring-2
                ${className}`}
              aria-invalid={controlProps['aria-invalid']}
              aria-describedby={controlProps['aria-describedby']}
              {...props}
            />
          );
        }}
      </FormField>
    );
  }
);

Input.displayName = 'Input';
